import eq from "lodash-es/eq"
import includes from "lodash-es/includes"

import { buildCssClassName, getTransitionTwClasses } from "@/3514/utils"

import {
    ECoachNavigationStepperBulletCompletion as ECompletion,
    ECoachNavigationStepperBulletOrder as EOrder,
    ECoachNavigationStepperMode as EStepperMode
} from "./coach.navigation-stepper.types"

function _getBulletBaseClasses(): string {
    return buildCssClassName(
        "rounded-full",
        "flex",
        "items-center",
        "justify-center",
        getTransitionTwClasses("all", 100)
    )
}

function _getBulletSizeClasses({ order }: { order: EOrder }): string {
    return buildCssClassName([
        eq(order, EOrder.Current) && ["min-w-[22px]", "w-[22px]", "min-h-[22px]", "h-[22px]"],
        eq(order, EOrder.NotCurrent) && ["min-w-[20px]", "w-[20px]", "min-h-[20px]", "h-[20px]"]
    ])
}

function _getDefaultBulletColorClasses({
    order,
    completion,
    stepperMode
}: {
    order: EOrder
    completion: ECompletion
    stepperMode: EStepperMode
}): string {
    return buildCssClassName([
        eq(order, EOrder.Current) && [
            "bg-white",
            "border-blue-200",
            "outline",
            "outline-[1px]",
            "outline-blue-200",
            "border-[1px]"
        ],
        eq(order, EOrder.NotCurrent) && [
            "bg-white",
            "border-blue-300",
            "outline-transparent",
            "outline-[1px]",
            "outline",
            [
                includes<EStepperMode>([EStepperMode.Sessions, EStepperMode.Modules], stepperMode) && [
                    "group-hover:outline-blue-300",
                    "group-focus:outline-blue-300"
                ]
            ],
            [eq(completion, ECompletion.Completed) && "border-[1px]"],
            [eq(completion, ECompletion.NotCompleted) && "border-[2px]"]
        ]
    ])
}

export default {
    stepper: {
        container: ({ mode }: { mode: EStepperMode }): string =>
            buildCssClassName(
                "w-full",
                "items-center",
                "flex",
                "gap-x-[15px]",
                includes<EStepperMode>([EStepperMode.Sessions, EStepperMode.Modules], mode) && [
                    "px-[5px] py-[5px] md:py-0 md:max-w-[800px] self-start"
                ],
                eq(mode, EStepperMode.Calendars) && ["mb-[40px]", "self-center"]
            ),
        list: (): string =>
            buildCssClassName(
                "w-full",
                "flex",
                "items-baseline",
                "overflow-x-auto",
                "scrollbar-thin-blue",
                "md:no-scrollbar",
                "relative"
            ),
        dividerSmall: ({
            isDesktop,
            isTablet,
            isModulesCut
        }: {
            isDesktop: boolean
            isTablet: boolean
            isModulesCut: boolean
        }): string =>
            buildCssClassName(
                [isTablet && [isModulesCut ? "max-w-[15px] min-w-[15px]" : "max-w-[2px] min-w-[2px]"]],
                [isDesktop && [isModulesCut ? "max-w-[20px] min-w-[20px]" : "max-w-[10px] min-w-[10px]"]]
            ),
        divider: ({ isSessionsCut, isModulesCut }: { isSessionsCut: boolean; isModulesCut: boolean }): string =>
            buildCssClassName(
                isSessionsCut
                    ? "max-[767px]:min-w-[30px] max-[767px]:max-w-[30px] min-w-[20px] max-w-[20px]"
                    : isModulesCut
                      ? "min-w-[40px] max-w-[40px] xl:min-w-[50px] xl:max-w-[50px]"
                      : "max-[767px]:min-w-[60px] max-[1199px]:min-w-[100px] min-w-[150px]"
            )
    },
    bulletHolder: (): string => "flex flex-col items-center",
    bulletContainer: ({ order, stepperMode }: { order: EOrder; stepperMode: EStepperMode }): string =>
        buildCssClassName(
            "flex items-center justify-center !outline-none px-[5px]",
            eq(order, EOrder.Current) && "pt-[2px]",
            includes<EStepperMode>([EStepperMode.Sessions, EStepperMode.Modules], stepperMode)
                ? "cursor-pointer group"
                : "cursor-auto"
        ),
    bullet: {
        default: ({
            order,
            completion,
            stepperMode
        }: {
            order: EOrder
            completion: ECompletion
            stepperMode: EStepperMode
        }): string =>
            buildCssClassName(
                _getBulletBaseClasses(),
                _getBulletSizeClasses({ order }),
                _getDefaultBulletColorClasses({ order, completion, stepperMode })
            )
    },
    bulletMark: ({ order, completion }: { order: EOrder; completion: ECompletion }): string => {
        const _isNotCompleted: boolean = completion === ECompletion.NotCompleted
        const _isCompleted: boolean = completion === ECompletion.Completed
        const _isCurrent: boolean = order === EOrder.Current
        const _isNotCurrent: boolean = order === EOrder.NotCurrent

        return buildCssClassName(
            "rounded-full",
            { "bg-blue-200": _isCurrent },
            { "bg-blue-300": _isNotCurrent && _isCompleted },
            { "opacity-[0]": _isNotCompleted },
            { "opacity-[1]": _isCompleted },
            { "w-[8px] h-[8px]": _isCurrent },
            { "w-[6px] h-[6px]": _isNotCurrent },
            getTransitionTwClasses("all", 100)
        )
    },
    bulletTitleContainer: (): string => "flex flex-col items-center max-w-[1px] mt-[4px]",
    bulletText: (): string => "min-w-[120px]"
}
